<template>
  <section class="news-page page-stack">
    <article class="card page-hero-card news-hero">
      <header class="page-hero-head">
        <div class="page-hero-copy">
          <p class="eyebrow">Wowhead RSS</p>
          <h2>News</h2>
          <p>Official blue posts and Wowhead news collected into a focused Warcraft feed.</p>
        </div>
        <a class="btn-outline hero-feed-link" :href="rssUrl" target="_blank" rel="noreferrer">
          <i class="fa-solid fa-rss"></i>
          RSS
        </a>
      </header>
    </article>

    <div class="card filter-toolbar news-toolbar">
      <div class="applied-filters">
        <span class="filter-pill">Posts: <span>{{ posts.length }}</span></span>
        <span class="filter-pill">Feed: <span>{{ activeFeed.label }}</span></span>
        <span v-if="lastUpdatedLabel" class="filter-pill">Updated: <span>{{ lastUpdatedLabel }}</span></span>
        <span v-if="search" class="filter-pill">Search: <span>{{ search }}</span></span>
      </div>
      <div role="group" class="quick-search-field">
        <input
          v-model.trim="search"
          class="input"
          aria-label="Search Wowhead feed"
          placeholder="Search Wowhead feed"
          type="search"
        />
      </div>
      <div class="page-actions">
        <div class="view-switch" aria-label="News layout">
          <button
            class="btn-sm-outline"
            :class="{ 'is-active': viewMode === 'grid' }"
            type="button"
            aria-label="Grid layout"
            title="Grid layout"
            @click="viewMode = 'grid'"
          >
            <i class="fa-solid fa-grip"></i>
          </button>
          <button
            class="btn-sm-outline"
            :class="{ 'is-active': viewMode === 'list' }"
            type="button"
            aria-label="List layout"
            title="List layout"
            @click="viewMode = 'list'"
          >
            <i class="fa-solid fa-list"></i>
          </button>
        </div>
        <button class="btn-icon-outline" type="button" aria-label="Refresh feed" title="Refresh feed" :disabled="loading" @click="loadPosts">
          <i class="fa-solid fa-rotate"></i>
        </button>
      </div>
    </div>

    <div class="card wowhead-feed-tabs" aria-label="Wowhead feed sources">
      <button
        v-for="feed in visibleFeeds"
        :key="feed.key"
        class="btn-sm-outline"
        :class="{ 'is-active': activeFeedKey === feed.key }"
        type="button"
        @click="selectFeed(feed.key)"
      >
        {{ feed.label }}
      </button>
    </div>

    <div v-if="loading" class="news-grid" :class="{ 'is-list-view': viewMode === 'list' }">
      <article v-for="index in 6" :key="index" class="card news-card news-skeleton">
        <span class="skeleton-media news-banner-skeleton"></span>
        <span class="skeleton-line skeleton-line-short"></span>
        <span class="skeleton-line"></span>
        <span class="skeleton-line skeleton-line-medium"></span>
      </article>
    </div>

    <AppEmptyState
      v-else-if="loadError"
      eyebrow="News"
      icon="fa-solid fa-triangle-exclamation"
      title="Unable to load the feed"
      :message="loadError"
    />

    <AppEmptyState
      v-else-if="!filteredPosts.length"
      eyebrow="No matches"
      icon="fa-solid fa-magnifying-glass"
      title="No posts found"
      message="Try a different search phrase, switch feed source, or refresh the feed."
    />

    <div v-else class="news-grid" :class="{ 'is-list-view': viewMode === 'list' }">
      <article
        v-for="post in filteredPosts"
        :key="post.id"
        class="card news-card"
        :class="{ 'is-news-card': post.kind === 'news', 'is-blue-card': post.kind === 'blue', 'has-banner': post.imageUrl }"
      >
        <div class="wowhead-card-banner" :class="{ 'is-blue-banner': post.kind === 'blue', 'is-empty-banner': !post.imageUrl && post.kind !== 'blue' }">
          <a v-if="post.imageUrl" class="wowhead-news-banner" :href="post.link" target="_blank" rel="noreferrer">
            <img :src="post.imageUrl" :alt="post.title" loading="lazy">
          </a>
          <div v-else-if="post.kind === 'blue'" class="wowhead-blue-banner" aria-hidden="true">
            <i class="fa-brands fa-battle-net"></i>
          </div>
          <div v-else class="wowhead-news-banner-placeholder" aria-hidden="true"></div>
          <span class="wowhead-category-badge">{{ post.category }}</span>
        </div>

        <header class="news-card-head">
          <div>
            <h3>
              <a :href="post.link" target="_blank" rel="noreferrer">{{ post.title }}</a>
            </h3>
          </div>
        </header>

        <p class="news-card-summary">{{ post.summary || 'Open the Wowhead post to read the full update.' }}</p>

        <footer class="news-card-foot">
          <time v-if="post.publishedAt" :datetime="post.publishedAt">{{ formatDate(post.publishedAt) }}</time>
          <span v-else>Recent update</span>
          <a class="ranking-link" :href="post.link" target="_blank" rel="noreferrer">
            Read post
            <i class="fa-solid fa-arrow-up-right-from-square"></i>
          </a>
        </footer>
      </article>
    </div>
  </section>
</template>

<script>
import AppEmptyState from '../components/AppEmptyState.vue'
import { WOWHEAD_NEWS_FEEDS, getWowheadArticleImage, getWowheadFeedPosts } from '../services/wowheadNews'

const NEWS_STATE_KEY = 'rio-dashboard-news-state'
const DEFAULT_NEWS_STATE = {
  viewMode: 'grid',
}

function loadNewsState() {
  try {
    const saved = JSON.parse(localStorage.getItem(NEWS_STATE_KEY) || '{}')
    return {
      viewMode: ['grid', 'list'].includes(saved.viewMode) ? saved.viewMode : DEFAULT_NEWS_STATE.viewMode,
    }
  } catch (error) {
    return { ...DEFAULT_NEWS_STATE }
  }
}

export default {
  name: 'NewsView',
  components: {
    AppEmptyState,
  },
  data() {
    const savedState = loadNewsState()

    return {
      posts: [],
      search: '',
      loading: false,
      loadError: '',
      lastUpdatedAt: '',
      activeFeedKey: 'all',
      viewMode: savedState.viewMode,
      feeds: WOWHEAD_NEWS_FEEDS,
    }
  },
  computed: {
    visibleFeeds() {
      return this.feeds.filter((feed) => ['aggregate', 'blue', 'news'].includes(feed.kind))
    },
    activeFeed() {
      return this.feeds.find((feed) => feed.key === this.activeFeedKey) || this.feeds[0]
    },
    rssUrl() {
      return this.activeFeed.url
    },
    filteredPosts() {
      const query = this.search.toLowerCase()
      if (!query) return this.posts

      return this.posts.filter((post) => {
        return [post.title, post.summary, post.category].some((value) => String(value || '').toLowerCase().includes(query))
      })
    },
    lastUpdatedLabel() {
      return this.lastUpdatedAt ? this.formatTime(this.lastUpdatedAt) : ''
    },
  },
  mounted() {
    this.loadPosts()
  },
  methods: {
    selectFeed(feedKey) {
      if (this.activeFeedKey === feedKey || this.loading) return
      this.activeFeedKey = feedKey
      this.search = ''
      this.loadPosts()
    },
    async loadPosts() {
      this.loading = true
      this.loadError = ''
      const loadingId = await this.$store.dispatch('startGlobalLoading', `Loading ${this.activeFeed.label}...`)

      try {
        const posts = await getWowheadFeedPosts(this.activeFeedKey)
        this.posts = this.activeFeed.kind === 'blue' ? posts : await this.enrichNewsBanners(posts)
        this.lastUpdatedAt = new Date().toISOString()
      } catch (error) {
        this.loadError = 'Wowhead RSS is unavailable right now. You can still open the RSS source directly from the header.'
        this.$store.dispatch('notify', {
          type: 'danger',
          title: `${this.activeFeed.label} unavailable`,
          description: this.loadError,
        })
      } finally {
        this.loading = false
        this.$store.dispatch('finishGlobalLoading', loadingId)
      }
    },
    async enrichNewsBanners(posts) {
      const bannerLimit = 12
      const enrichedPosts = [...posts]
      const targets = enrichedPosts.filter((post) => post.kind === 'news').slice(0, bannerLimit)
      const results = await Promise.allSettled(
        targets.map(async (post) => {
          if (post.imageUrl || !post.link) return post
          return {
            ...post,
            imageUrl: await getWowheadArticleImage(post.link),
          }
        })
      )

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const postIndex = enrichedPosts.findIndex((post) => post.id === result.value.id)
          if (postIndex !== -1) {
            enrichedPosts[postIndex] = result.value
          }
        }
      })

      return enrichedPosts
    },
    formatDate(value) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
    formatTime(value) {
      return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    },
    persistNewsState() {
      try {
        localStorage.setItem(NEWS_STATE_KEY, JSON.stringify({
          viewMode: this.viewMode,
        }))
      } catch (error) {
        console.error('Unable to save News state', error)
      }
    },
  },
  watch: {
    viewMode() {
      this.persistNewsState()
    },
  },
}
</script>
